import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { GpuBudget } from '../types/models/GpuBudget';
import type { ApiResponseGpuBudget } from '../types/api/ApiResponseGpuBudget';
import { isAbortError, readJsendBody } from './gpuServerResponse';
import { budgetReadErrorMessage, isBudgetNotFound } from './gpuBudgetResponse';

export interface UseGpuBudgetReturn {
  /** The budget of `id` — `null` while it loads, when `id` is null, or when it is gone. */
  budget: GpuBudget | null;
  /** True during the load of a newly selected id AND a background `refetch`. */
  loading: boolean;
  /** The last read's failure for `id` (other than not-found); the last good `budget` is kept. */
  error: string | null;
  /** Re-read `id` in the background: the current budget stays on screen until the answer arrives. */
  refetch: () => Promise<void>;
  /** Replace the budget with the one a write returned (no extra GET); aborts a read in flight. */
  setBudget: (budget: GpuBudget) => void;
}

/** A budget-shaped payload (the rendering relies on these arrays and objects). */
export const isGpuBudget = (value: unknown): value is GpuBudget => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const b = value as Partial<GpuBudget>;
  return (
    typeof b.id === 'number' &&
    Array.isArray(b.lines) &&
    Array.isArray(b.subtotals) &&
    Array.isArray(b.warnings) &&
    typeof b.totals === 'object' &&
    b.totals !== null
  );
};

/**
 * `GET /datacenters/gpu-servers/budgets/{id}` — one budget, priced at today's prices
 * (docs/api-types/gpu-budgets.md). Database-only, never polled.
 *
 * Loads when `id` changes (to a non-null value), except when `setBudget` already primed that id (a
 * create returns the new budget: no extra GET). Then only on `refetch`. `apiFetch` is read through a
 * ref, so an OIDC silent renew never re-runs it.
 *
 * "Budget not found" (a colleague deleted it) calls `onNotFound(id)` once and clears the budget; the
 * caller must select another id — this hook never re-GETs the same id on its own.
 */
export const useGpuBudget = (id: number | null, onNotFound?: (id: number) => void): UseGpuBudgetReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);
  const onNotFoundRef = useRef(onNotFound);
  useEffect(() => {
    onNotFoundRef.current = onNotFound;
  }, [onNotFound]);

  const [budget, setBudgetState] = useState<GpuBudget | null>(null);
  const [loading, setLoading] = useState<boolean>(id !== null);
  const [error, setError] = useState<{ id: number; message: string } | null>(null);
  const budgetRef = useRef<GpuBudget | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestedIdRef = useRef<number | null>(null);

  const load = useCallback(async (targetId: number) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRef.current(`/datacenters/gpu-servers/budgets/${targetId}`, {
        signal: controller.signal,
      });
      const body = await readJsendBody<ApiResponseGpuBudget>(response);
      if (controller.signal.aborted) {
        return;
      }
      const candidate = body?.status === 'success' ? body.data?.budget : undefined;
      if (isGpuBudget(candidate) && candidate.id === targetId) {
        budgetRef.current = candidate;
        setBudgetState(candidate);
        return;
      }
      if (isBudgetNotFound(body)) {
        budgetRef.current = null;
        setBudgetState(null);
        onNotFoundRef.current?.(targetId);
        return;
      }
      setError({ id: targetId, message: budgetReadErrorMessage(body, 'Could not load this budget.') });
    } catch (err) {
      if (isAbortError(err, controller.signal)) {
        return;
      }
      setError({
        id: targetId,
        message: err instanceof Error ? err.message : 'An error occurred while loading this budget.',
      });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (id === null) {
      requestedIdRef.current = null;
      abortRef.current?.abort();
      abortRef.current = null;
      setLoading(false);
      return;
    }
    if (!auth.isAuthenticated || requestedIdRef.current === id) {
      return;
    }
    requestedIdRef.current = id;
    if (budgetRef.current?.id === id) {
      // Primed by setBudget (e.g. the budget a create returned).
      return;
    }
    void load(id);
  }, [id, auth.isAuthenticated, load]);

  // Forgetting the requested id makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      requestedIdRef.current = null;
    },
    [],
  );

  const refetch = useCallback(async () => {
    if (id !== null) {
      await load(id);
    }
  }, [id, load]);

  const setBudget = useCallback((next: GpuBudget) => {
    // A read that started before the write could answer with the older state: drop it.
    if (abortRef.current !== null) {
      abortRef.current.abort();
      abortRef.current = null;
      setLoading(false);
    }
    budgetRef.current = next;
    setBudgetState(next);
    setError(null);
  }, []);

  return {
    budget: budget !== null && budget.id === id ? budget : null,
    loading: id !== null && loading,
    error: error !== null && error.id === id ? error.message : null,
    refetch,
    setBudget,
  };
};

export default useGpuBudget;
